// Front/Missive-style email-as-channel renderer (channel_kind='email').
//
// Layout:
//   - Top toolbar: compose / refresh / mark-all-read / search box + view
//     chips (Inbox / Starred / Archived / Trash).
//   - Two-pane ≥768px: thread list (left, ~360px) → detail (right).
//   - Single-pane <768px: list → tap → detail with back arrow.
//
// Outbound messages get a left-border accent. Inline quick-reply at the
// bottom of the detail pane, plus Reply / Reply All / Forward buttons.
// Compose opens a full-pane compose view in the detail section (Gmail-style).

import { get, post, getBlob } from '../api';
import { state } from '../state';
import { showToast } from '../components/toast';
import DOMPurify from 'dompurify';

function toast(msg: string, kind: 'success' | 'error'): void {
  showToast(msg, kind === 'error' ? 'err' : 'ok');
}

interface ThreadSummary {
  id: string;
  channel_id: string;
  subject: string;
  subject_normalized: string;
  participants: string[];
  last_message_at: string;
  created_at: string;
  snippet: string;
  last_direction: string;
  last_from: string;
  message_count: number;
  starred: boolean;
  archived: boolean;
  unread: boolean;
  deleted_at: string | null;
}

interface EmailAttachment {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  content_id: string;
  is_inline: boolean;
}

interface ThreadMessage {
  id: string;
  direction: 'inbound' | 'outbound';
  message_id: string;
  in_reply_to: string;
  from: string;
  to: string;
  cc: string;
  subject: string;
  body_text: string;
  body_html: string;
  received_at: string;
  attachments?: EmailAttachment[];
}

interface ThreadDetail {
  id: string;
  channel_id: string;
  subject_normalized: string;
  participants: string[];
  last_message_at: string;
  created_at: string;
  starred: boolean;
  archived: boolean;
  unread: boolean;
  deleted_at: string | null;
  loi_gated: boolean;
  messages: ThreadMessage[];
}

type View = 'inbox' | 'starred' | 'archived' | 'trash' | 'drafts';

interface EmailDraft {
  id: string;
  channel_id: string;
  reply_to_thread_id: string | null;
  reply_mode: string;
  to_json: string;
  cc_json: string;
  bcc_json: string;
  subject: string;
  body_text: string;
  body_html: string;
  updated_at: string;
  created_at: string;
}

let pollTimer: ReturnType<typeof setInterval> | null = null;
let draftAutoSaveTimer: ReturnType<typeof setTimeout> | null = null;
let activeDraftId: string | null = null;
let activeChannelId: string | null = null;
let activeThreadId: string | null = null;
let activeView: View = 'inbox';
let currentThreads: ThreadSummary[] = [];
let searchTerm = '';
const scrollByThread = new Map<string, number>();

export function cleanupEmailInbox(): void {
  if (pollTimer) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
  if (draftAutoSaveTimer) {
    clearTimeout(draftAutoSaveTimer);
    draftAutoSaveTimer = null;
  }
  activeDraftId = null;
  activeChannelId = null;
  activeThreadId = null;
  activeView = 'inbox';
  searchTerm = '';
  currentThreads = [];
  scrollByThread.clear();
  cachedIdentities = null;
  document.getElementById('gmail-compose-popup')?.remove();
}

function esc(s: string): string {
  const el = document.createElement('div');
  el.textContent = s;
  return el.innerHTML;
}

function relativeTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d`;
  // Past a week → fall back to AEST date.
  return d.toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', day: 'numeric', month: 'short' });
}

// Friendly "name" from a raw address field. If the field is `Name <addr>`,
// return `Name`; otherwise the addr as-is.
function senderName(raw: string): string {
  const s = (raw || '').trim();
  const m = s.match(/^\s*(.+?)\s*<([^>]+)>\s*$/);
  if (m && m[1]) return m[1].replace(/^"|"$/g, '');
  return s;
}

// Convert bare https/http URLs into clickable <a target=_blank>. Conservative
// URL regex — only http(s)://, stops at whitespace and trailing punctuation.
function autoLink(text: string): string {
  const escaped = esc(text);
  return escaped.replace(
    /(https?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)])/g,
    '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>',
  );
}

// Plain-text quote handling — wrap consecutive lines starting with `>` in
// <blockquote class="email-quote">. Strips one leading `>` (and optional
// space) per nesting level so nested quotes render as nested blockquotes
// (Gmail/Missive 1:1: gradually-indented muted blocks instead of inline noise).
function renderPlainBody(text: string): string {
  const lines = (text || '(empty)').split('\n');
  type Block = { depth: number; lines: string[] };
  const blocks: Block[] = [];
  for (const line of lines) {
    let depth = 0;
    let rest = line;
    while (rest.startsWith('>')) {
      depth += 1;
      rest = rest.slice(1);
      if (rest.startsWith(' ')) rest = rest.slice(1);
    }
    const last = blocks[blocks.length - 1];
    if (last && last.depth === depth) {
      last.lines.push(rest);
    } else {
      blocks.push({ depth, lines: [rest] });
    }
  }
  // Render: depth 0 = autolinked plain text. depth N = N nested blockquotes
  // around the autolinked content. Joining blocks back with newlines so
  // pre-wrap whitespace stays intact across block boundaries.
  return blocks.map(b => {
    const inner = autoLink(b.lines.join('\n'));
    if (b.depth === 0) return inner;
    let html = inner;
    for (let i = 0; i < b.depth; i += 1) {
      html = `<blockquote class="email-quote">${html}</blockquote>`;
    }
    return html;
  }).join('\n');
}

// HTML body sanitiser — DOMPurify with a strict allowlist. DOMPurify is the
// de-facto XSS sanitiser for rich-HTML bodies (Bleach/Angular both ship with
// it under the hood); it closes the holes the hand-written DOMParser walker
// missed, e.g. `formaction`, `srcdoc`, mutation XSS via nested comments.
//
// `<style>` is ALLOWED (DOMPurify sanitises the CSS — strips expression(),
// @import, javascript: urls) because real-world invoices / marketing email
// (Regus, IONOS, …) carry their entire layout in a document-level <style>
// block with class selectors and no inline colours. Stripping it left an
// unstyled wall of text that inherited the dark app theme, which is exactly
// the "bodies unreadable" complaint. The HTML is rendered inside a sandboxed
// iframe (see renderHtmlBodyFrame) so the preserved <style> can only style
// the isolated email document, never the hub app.
//
// Post-hook rewrites:
//   - <a> gets target=_blank + rel=noopener so embedded links can't navigate
//     the hub app frame or steal window.opener.
//   - <img> gets loading="lazy" referrerpolicy="no-referrer" so remote
//     tracking pixels don't beacon back with user-identifying Referer, and
//     images are only fetched when the user actually scrolls to them.
let purifyConfigured = false;
function configurePurifyOnce(): void {
  if (purifyConfigured) return;
  DOMPurify.addHook('afterSanitizeAttributes', (node: Element) => {
    if (node.tagName === 'A') {
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    } else if (node.tagName === 'IMG') {
      node.setAttribute('loading', 'lazy');
      node.setAttribute('referrerpolicy', 'no-referrer');
    }
  });
  purifyConfigured = true;
}

function sanitizeEmailHtml(html: string): string {
  if (!html) return '';
  configurePurifyOnce();
  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    // `style` intentionally NOT forbidden — see comment above. Scripts and
    // navigation/embedding vectors stay forbidden; the iframe sandbox is the
    // second layer that neutralises anything the sanitiser would miss.
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'link', 'meta', 'base', 'form'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onfocus', 'onmouseover', 'srcdoc', 'formaction', 'action'],
  });
}

// Render a sanitised HTML email body into a sandboxed, white-background iframe
// and auto-size it to its content — the Gmail / Front / Missive 1:1 approach.
// The iframe gives the email its own document so its <style> + table layout
// render exactly as authored (light canvas, dark text) regardless of the hub's
// dark theme, and `sandbox` (no allow-scripts) means even a sanitiser miss
// can't run JS or reach the parent. Links open in a new tab via <base>.
function mountHtmlBodyFrames(root: ParentNode): void {
  root.querySelectorAll<HTMLIFrameElement>('iframe.email-html-frame').forEach(frame => {
    const encoded = frame.dataset['html'] ?? '';
    if (!encoded) return;
    let bodyHtml = '';
    try {
      bodyHtml = decodeURIComponent(encoded);
    } catch {
      bodyHtml = '';
    }
    // A minimal document shell: white canvas, dark text, sensible defaults that
    // the email's own <style> can override. <base target=_blank> sends every
    // link to a new tab (the sandbox blocks top-level navigation anyway).
    const srcdoc = `<!DOCTYPE html><html><head><meta charset="utf-8">`
      + `<base target="_blank">`
      + `<style>`
      + `html,body{margin:0;padding:0;background:#ffffff;color:#1a1a1a;`
      + `font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif;`
      + `font-size:15px;line-height:1.5;-webkit-text-size-adjust:100%;}`
      + `body{padding:16px;box-sizing:border-box;word-break:break-word;overflow-wrap:break-word;}`
      + `img{max-width:100%;height:auto;}`
      + `table{max-width:100%;}`
      + `a{color:#1d6fd0;}`
      + `</style></head><body>${bodyHtml}</body></html>`;
    frame.setAttribute('sandbox', 'allow-same-origin allow-popups allow-popups-to-escape-sandbox');
    frame.srcdoc = srcdoc;
    const resize = (): void => {
      const doc = frame.contentDocument;
      if (!doc || !doc.body) return;
      const h = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
      if (h > 0) frame.style.height = `${h + 4}px`;
    };
    frame.addEventListener('load', () => {
      resize();
      // Late-loading images can grow the document after first paint.
      const doc = frame.contentDocument;
      doc?.querySelectorAll('img').forEach(img => {
        if (!img.complete) img.addEventListener('load', resize, { once: true });
      });
      // One delayed pass catches webfont / reflow settling.
      window.setTimeout(resize, 300);
    });
  });
}

function findChannelId(): string | null {
  const ch = String(state.currentChannel || '');
  const sid = state.currentServer || '';
  const row = state.channels.find(c => c.name === ch && c.server_id === sid);
  return row?.id ?? null;
}

function isMobile(): boolean {
  return window.matchMedia('(max-width: 767px)').matches;
}

async function fetchThreads(channelId: string, view: View): Promise<ThreadSummary[]> {
  try {
    const qs = `channel_id=${encodeURIComponent(channelId)}&view=${encodeURIComponent(view)}&limit=100`;
    const data = await get<{ threads: ThreadSummary[] }>(`/api/email-threads?${qs}`);
    return data.threads ?? [];
  } catch {
    return [];
  }
}

async function fetchSearch(channelId: string, q: string): Promise<ThreadSummary[]> {
  if (!q.trim()) return [];
  try {
    const qs = `channel_id=${encodeURIComponent(channelId)}&q=${encodeURIComponent(q)}&limit=100`;
    const data = await get<{ threads: ThreadSummary[] }>(`/api/email-threads/search?${qs}`);
    return data.threads ?? [];
  } catch {
    return [];
  }
}

async function fetchThread(id: string): Promise<ThreadDetail | null> {
  try {
    return await get<ThreadDetail>(`/api/email-threads/${encodeURIComponent(id)}`);
  } catch {
    return null;
  }
}

function threadRow(t: ThreadSummary, selected: boolean): string {
  const displayName = senderName(t.last_from) || (t.participants[0] ?? '(unknown)');
  const subject = t.subject || t.subject_normalized || '(no subject)';
  const unreadClass = t.unread ? ' email-row-unread' : '';
  const starCls = t.starred ? ' email-row-star-on' : '';
  const arrow = t.last_direction === 'outbound' ? '\u2197' : '\u2199';
  const countBadge = t.message_count > 1
    ? `<span class="email-row-count">${t.message_count}</span>`
    : '';
  return `<div class="email-row${selected ? ' selected' : ''}${unreadClass}" data-thread-id="${esc(t.id)}" role="button" tabindex="0">
    <div class="email-row-top">
      <span class="email-row-dot" aria-hidden="true"></span>
      <span class="email-row-from" title="${esc(t.last_from || '')}">${esc(displayName)}</span>
      <span class="email-row-time">${esc(relativeTime(t.last_message_at))}</span>
    </div>
    <div class="email-row-mid">
      <span class="email-row-subject">${esc(subject)}</span>
      ${countBadge}
    </div>
    <div class="email-row-bottom">
      <span class="email-row-arrow" aria-hidden="true">${arrow}</span>
      <span class="email-row-snippet">${esc(t.snippet || '')}</span>
      <button class="email-row-star${starCls}" data-action="star" data-thread-id="${esc(t.id)}" aria-label="${t.starred ? 'Unstar' : 'Star'}">${t.starred ? '\u2605' : '\u2606'}</button>
    </div>
  </div>`;
}

function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

// A paperclip SVG (no emoji per house rule) + filename + size, one chip per
// attachment. Gmail/Front/Missive 1:1: attachments sit in a strip under the
// body. Click streams the bytes via an authenticated fetch (downloadAttachment).
function attachmentStrip(attachments: EmailAttachment[] | undefined): string {
  if (!attachments || attachments.length === 0) return '';
  const chips = attachments.map(a => {
    const size = formatBytes(a.size_bytes);
    const sizeLabel = size ? `<span class="email-attach-size">${esc(size)}</span>` : '';
    return `<button type="button" class="email-attach-chip" data-att-id="${esc(a.id)}" data-att-name="${esc(a.filename)}" title="Download ${esc(a.filename)}">
      <svg class="email-attach-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
      <span class="email-attach-name">${esc(a.filename)}</span>
      ${sizeLabel}
    </button>`;
  }).join('');
  return `<div class="email-attach-strip">${chips}</div>`;
}

// Stream an attachment's bytes via an authenticated request and trigger a
// browser download. Using a blob + object URL keeps the Bearer token out of
// the URL bar and history (a plain <a href> can't send the auth header).
async function downloadAttachment(id: string, filename: string): Promise<void> {
  try {
    const blob = await getBlob(`/api/email-attachments/${encodeURIComponent(id)}`);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'attachment';
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Revoke on the next tick so the download has a chance to start.
    window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch {
    showToast('Could not download attachment', 'err');
  }
}

// ── Compose attachments (outbound upload) ────────────────────────────────
// Email send endpoints accept attachments as JSON base64 `{filename, content}`
// (matching email_send.rs / the inbound IncomingAttachment contract), so we
// read each picked file to a base64 string. Caps mirror the backend: 20MB per
// file, 38MB total per message (staying under Resend's ~40MB).
interface PendingAttachment {
  filename: string;
  content: string; // base64 (no data: prefix)
  size: number;
}

const MAX_ATTACH_BYTES = 20 * 1024 * 1024;
const MAX_ATTACH_TOTAL_BYTES = 38 * 1024 * 1024;

// Read a File to bare base64 (strip the `data:<mime>;base64,` prefix that
// FileReader.readAsDataURL produces) — the wire contract is raw base64.
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || '');
      const comma = result.indexOf(',');
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

// Append picked files to a pending list, enforcing the per-file + total caps.
// Returns the updated list; surfaces a toast on any rejected file.
async function addPendingFiles(existing: PendingAttachment[], files: FileList | File[]): Promise<PendingAttachment[]> {
  const next = [...existing];
  let total = next.reduce((sum, a) => sum + a.size, 0);
  for (const file of Array.from(files)) {
    if (file.size > MAX_ATTACH_BYTES) {
      showToast(`${file.name} exceeds the 20MB per-file limit`, 'err');
      continue;
    }
    if (total + file.size > MAX_ATTACH_TOTAL_BYTES) {
      showToast('Attachments exceed the 38MB total limit', 'err');
      break;
    }
    try {
      const content = await fileToBase64(file);
      next.push({ filename: file.name, content, size: file.size });
      total += file.size;
    } catch {
      showToast(`Could not read ${file.name}`, 'err');
    }
  }
  return next;
}

// Render the pending-attachment chip strip (paperclip + name + size + remove).
// Mirrors chat.ts's renderAttachPreview. `onRemove` is wired by the caller.
function renderPendingChips(host: HTMLElement, pending: PendingAttachment[], onRemove: (idx: number) => void): void {
  if (pending.length === 0) {
    host.style.display = 'none';
    host.innerHTML = '';
    return;
  }
  host.style.display = 'flex';
  host.innerHTML = pending.map((a, i) => {
    const size = formatBytes(a.size);
    const sizeLabel = size ? `<span class="email-attach-size">${esc(size)}</span>` : '';
    return `<span class="email-attach-chip email-attach-pending">
      <svg class="email-attach-icon" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
      <span class="email-attach-name">${esc(a.filename)}</span>
      ${sizeLabel}
      <button type="button" class="email-attach-remove" data-idx="${i}" title="Remove ${esc(a.filename)}" aria-label="Remove ${esc(a.filename)}">&times;</button>
    </span>`;
  }).join('');
  host.querySelectorAll<HTMLButtonElement>('.email-attach-remove').forEach(btn => {
    btn.addEventListener('click', () => {
      const idx = parseInt(btn.dataset['idx'] ?? '-1', 10);
      if (idx >= 0) onRemove(idx);
    });
  });
}

function messageBlock(m: ThreadMessage, isLast: boolean, collapsible: boolean = false, collapsed: boolean = false): string {
  const ts = m.received_at ? new Date(m.received_at).toLocaleString() : '';
  const safeHtml = sanitizeEmailHtml(m.body_html);
  // HTML bodies render in a sandboxed white-canvas iframe (Gmail/Front/Missive
  // 1:1) so emails authored for white backgrounds stay readable under the dark
  // app theme. The sanitised markup rides along URI-encoded in a data attr and
  // is injected as srcdoc by mountHtmlBodyFrames after the node is in the DOM.
  // Plain-text bodies keep the inline rendered path.
  const bodyHtml = safeHtml
    ? `<div class="email-msg-body email-msg-body-html"><iframe class="email-html-frame" title="Email content" scrolling="no" data-html="${esc(encodeURIComponent(safeHtml))}"></iframe></div>`
    : `<div class="email-msg-body email-msg-body-text">${renderPlainBody(m.body_text)}</div>`;
  const arrow = m.direction === 'outbound' ? '\u2197' : '\u2199';
  const fromLabel = m.direction === 'outbound' ? 'You' : senderName(m.from) || m.from;
  const ccRow = m.cc
    ? `<div class="email-msg-meta-row"><span class="email-msg-label">Cc</span><span>${esc(m.cc)}</span></div>`
    : '';
  const lastMarker = isLast ? ' email-msg-last' : '';
  const collapsedCls = collapsed ? ' email-msg-collapsed' : '';
  const snippetText = (m.body_text || '').replace(/[\n\r]+/g, ' ').trim();
  const snippet = snippetText.length > 80 ? snippetText.slice(0, 80) + '\u2026' : snippetText;
  const chevron = collapsible
    ? '<svg class="email-msg-chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>'
    : '';
  return `<article class="email-msg email-msg-${esc(m.direction)}${lastMarker}${collapsedCls}" data-msg-id="${esc(m.id)}">
    <header class="email-msg-head${collapsible ? ' email-msg-head-toggle' : ''}">
      <span class="email-msg-arrow" aria-hidden="true">${arrow}</span>
      <div class="email-msg-meta">
        <div class="email-msg-meta-row">
          <span class="email-msg-from"><strong>${esc(fromLabel)}</strong></span>
          <span class="email-msg-time">${esc(ts)}</span>
        </div>
        <div class="email-msg-meta-row"><span class="email-msg-label">To</span><span>${esc(m.to)}</span></div>
        ${ccRow}
        <div class="email-msg-snippet">${esc(snippet)}</div>
      </div>
      ${chevron}
    </header>
    ${bodyHtml}
    ${attachmentStrip(m.attachments)}
  </article>`;
}

function emptyState(text: string, ctaLabel?: string): string {
  const cta = ctaLabel
    ? `<button class="btn btn-primary" id="email-empty-cta" type="button">${esc(ctaLabel)}</button>`
    : '';
  return `<div class="email-empty"><p>${esc(text)}</p>${cta}</div>`;
}

function renderThreadList(target: HTMLElement, threads: ThreadSummary[]): void {
  currentThreads = threads;
  if (threads.length === 0) {
    const label = activeView === 'inbox'
      ? 'No emails yet. Send the first one with Compose.'
      : `No threads in ${activeView}.`;
    target.innerHTML = emptyState(label, activeView === 'inbox' ? 'Compose' : undefined);
    document.getElementById('email-empty-cta')?.addEventListener('click', () => {
      activeDraftId = null;
      openComposePopup({ to: '', cc: '', bcc: '', subject: '', body: '', isHtml: false });
    });
    return;
  }
  target.innerHTML = threads.map(t => threadRow(t, t.id === activeThreadId)).join('');

  // Row click → open thread. Star button stops propagation.
  target.querySelectorAll<HTMLElement>('.email-row').forEach(row => {
    const open = (): void => {
      const id = row.dataset['threadId'] ?? '';
      if (!id) return;
      activeThreadId = id;
      target.querySelectorAll('.email-row.selected').forEach(n => n.classList.remove('selected'));
      row.classList.add('selected');
      row.classList.remove('email-row-unread');
      if (isMobile()) {
        document.getElementById('email-pane')?.classList.add('email-pane-show-detail');
      }
      void renderDetail();
    };
    row.addEventListener('click', (ev) => {
      const target = ev.target as HTMLElement;
      if (target.closest('[data-action]')) return;
      open();
    });
    row.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); }
    });
  });

  // Star toggle.
  target.querySelectorAll<HTMLButtonElement>('[data-action="star"]').forEach(btn => {
    btn.addEventListener('click', async (ev) => {
      ev.stopPropagation();
      const id = btn.dataset['threadId'] ?? '';
      if (!id) return;
      const t = currentThreads.find(x => x.id === id);
      if (!t) return;
      const next = !t.starred;
      try {
        await post(`/api/email-threads/${encodeURIComponent(id)}/star`, { value: next });
        t.starred = next;
        btn.classList.toggle('email-row-star-on', next);
        btn.textContent = next ? '\u2605' : '\u2606';
        btn.setAttribute('aria-label', next ? 'Unstar' : 'Star');
      } catch (e) {
        toast(e instanceof Error ? e.message : 'Star failed', 'error');
      }
    });
  });
}

function headerRow(thread: ThreadDetail): string {
  const subject = thread.subject_normalized || '(no subject)';
  const participants = thread.participants.length
    ? thread.participants.map(p => esc(p)).join(', ')
    : '(no participants)';
  const loiBadge = thread.loi_gated
    ? `<span class="email-loi-badge" title="Mitchell-only until 'send it' approval">LOI gate</span>`
    : '';
  const starLabel = thread.starred ? '\u2605 Starred' : '\u2606 Star';
  const archiveLabel = thread.archived ? 'Unarchive' : 'Archive';
  const trashLabel = thread.deleted_at ? 'Restore' : 'Delete';
  return `
    <div class="email-detail-head">
      <div class="email-detail-head-top">
        <button class="btn-icon email-back-btn" id="email-back-btn" type="button" aria-label="Back to list">&larr;</button>
        <div class="email-detail-subject">${esc(subject)} ${loiBadge}</div>
      </div>
      <div class="email-detail-participants">${participants}</div>
      <div class="email-detail-actions">
        <button class="btn btn-sm" data-act="reply" type="button">Reply</button>
        <button class="btn btn-sm" data-act="reply-all" type="button">Reply all</button>
        <button class="btn btn-sm" data-act="forward" type="button">Forward</button>
        <button class="btn btn-sm" data-act="star" type="button">${starLabel}</button>
        <button class="btn btn-sm" data-act="archive" type="button">${archiveLabel}</button>
        <button class="btn btn-sm" data-act="unread" type="button">Mark unread</button>
        <button class="btn btn-sm btn-danger-ghost" data-act="delete" type="button">${trashLabel}</button>
      </div>
    </div>`;
}

async function renderDetail(): Promise<void> {
  const detailEl = document.getElementById('email-detail');
  if (!detailEl) return;
  if (!activeThreadId) {
    detailEl.innerHTML = emptyState('Select a thread on the left.');
    return;
  }
  detailEl.innerHTML = '<div class="email-empty"><p>Loading thread\u2026</p></div>';
  const thread = await fetchThread(activeThreadId);
  if (!thread) {
    detailEl.innerHTML = emptyState('Failed to load thread.');
    return;
  }

  const multi = thread.messages.length > 1;
  const msgs = thread.messages.map((m, i) => {
    const isLast = i === thread.messages.length - 1;
    return messageBlock(m, isLast, multi, multi && !isLast);
  }).join('');
  const loiField = thread.loi_gated
    ? `<input type="text" id="email-reply-approval" placeholder="Type 'send it' to confirm" autocomplete="off" />
       <div class="email-loi-warn">This thread is LOI-gated. Replies require explicit approval.</div>`
    : '';
  detailEl.innerHTML = `
    ${headerRow(thread)}
    <div class="email-detail-msgs" id="email-detail-msgs">${msgs}</div>
    <form class="email-reply" id="email-reply-form">
      <textarea id="email-reply-body" placeholder="Quick reply\u2026" rows="3" required></textarea>
      ${loiField}
      <div class="email-reply-actions">
        <span class="email-reply-status" id="email-reply-status"></span>
        <button type="button" class="btn btn-secondary btn-sm" id="email-reply-open-modal">Full compose</button>
        <button type="submit" class="btn btn-primary" id="email-reply-send">Send reply</button>
      </div>
    </form>
  `;

  // Inject sandboxed iframes for every HTML body now that the markup is live.
  const msgsContainer = document.getElementById('email-detail-msgs');
  if (msgsContainer) {
    mountHtmlBodyFrames(msgsContainer);
    // Delegated download: any attachment chip streams its bytes via an
    // authenticated fetch, then triggers a browser save (Gmail/Front 1:1).
    msgsContainer.addEventListener('click', ev => {
      const target = ev.target instanceof Element ? ev.target.closest('.email-attach-chip') : null;
      if (!(target instanceof HTMLElement)) return;
      const attId = target.dataset['attId'];
      const attName = target.dataset['attName'] ?? 'attachment';
      if (attId) void downloadAttachment(attId, attName);
    });
  }

  // Restore per-thread scroll, else start at top (chronological reading order).
  const msgsEl = document.getElementById('email-detail-msgs');
  if (msgsEl) {
    const saved = scrollByThread.get(thread.id);
    msgsEl.scrollTop = typeof saved === 'number' ? saved : 0;
    msgsEl.addEventListener('scroll', () => {
      scrollByThread.set(thread.id, msgsEl.scrollTop);
    });
    msgsEl.addEventListener('click', (e) => {
      const head = (e.target as HTMLElement).closest('.email-msg-head-toggle');
      if (!head) return;
      const article = head.closest('.email-msg') as HTMLElement | null;
      if (article) article.classList.toggle('email-msg-collapsed');
    });
  }

  document.getElementById('email-back-btn')?.addEventListener('click', () => {
    document.getElementById('email-pane')?.classList.remove('email-pane-show-detail');
  });

  detailEl.querySelectorAll<HTMLButtonElement>('.email-detail-actions [data-act]').forEach(btn => {
    btn.addEventListener('click', async () => {
      const act = btn.dataset['act'] ?? '';
      await handleAction(act, thread);
    });
  });

  document.getElementById('email-reply-open-modal')?.addEventListener('click', () => {
    openComposePaneFromThread('reply', thread);
  });

  const form = document.getElementById('email-reply-form') as HTMLFormElement | null;
  form?.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    const bodyEl = document.getElementById('email-reply-body') as HTMLTextAreaElement | null;
    const approvalEl = document.getElementById('email-reply-approval') as HTMLInputElement | null;
    const statusEl = document.getElementById('email-reply-status');
    const sendBtn = document.getElementById('email-reply-send') as HTMLButtonElement | null;
    if (!bodyEl) return;
    const body = bodyEl.value.trim();
    if (!body) {
      if (statusEl) statusEl.textContent = 'Body required.';
      return;
    }
    if (sendBtn) sendBtn.disabled = true;
    if (statusEl) statusEl.textContent = 'Sending\u2026';
    try {
      const lastInbound = [...thread.messages].reverse().find(m => m.direction === 'inbound');
      const inboundTo = lastInbound?.to ?? '';
      const ids = await loadIdentities();
      const fromMatch = findIdentityForAddress(ids, inboundTo);
      await post(`/api/email-threads/${encodeURIComponent(thread.id)}/reply`, {
        body_text: body,
        approval: approvalEl?.value ?? '',
        from_identity_id: fromMatch?.id ?? '',
      });
      if (statusEl) statusEl.textContent = 'Sent.';
      bodyEl.value = '';
      if (approvalEl) approvalEl.value = '';
      await renderDetail();
    } catch (e) {
      if (statusEl) statusEl.textContent = (e instanceof Error ? e.message : 'Send failed');
    } finally {
      if (sendBtn) sendBtn.disabled = false;
    }
  });
}

async function handleAction(act: string, thread: ThreadDetail): Promise<void> {
  switch (act) {
    case 'reply':
      openComposePaneFromThread('reply', thread);
      return;
    case 'reply-all':
      openComposePaneFromThread('reply-all', thread);
      return;
    case 'forward':
      openComposePaneFromThread('forward', thread);
      return;
    case 'star':
      try {
        await post(`/api/email-threads/${encodeURIComponent(thread.id)}/star`, { value: !thread.starred });
        await refresh();
        await renderDetail();
      } catch (e) { toast(e instanceof Error ? e.message : 'Star failed', 'error'); }
      return;
    case 'archive':
      try {
        await post(`/api/email-threads/${encodeURIComponent(thread.id)}/archive`, { value: !thread.archived });
        toast(thread.archived ? 'Unarchived' : 'Archived', 'success');
        if (activeView === 'inbox' && !thread.archived) {
          activeThreadId = null;
        }
        await refresh();
        if (activeThreadId) await renderDetail();
        else clearDetail();
      } catch (e) { toast(e instanceof Error ? e.message : 'Archive failed', 'error'); }
      return;
    case 'unread':
      try {
        await post(`/api/email-threads/${encodeURIComponent(thread.id)}/unread`, { value: true });
        toast('Marked unread', 'success');
        await refresh();
      } catch (e) { toast(e instanceof Error ? e.message : 'Mark unread failed', 'error'); }
      return;
    case 'delete':
      try {
        const restore = !!thread.deleted_at;
        await post(`/api/email-threads/${encodeURIComponent(thread.id)}/delete`, { restore });
        toast(restore ? 'Restored' : 'Moved to trash', 'success');
        if (!restore) activeThreadId = null;
        await refresh();
        if (activeThreadId) await renderDetail();
        else clearDetail();
      } catch (e) { toast(e instanceof Error ? e.message : 'Delete failed', 'error'); }
      return;
  }
}

function clearDetail(): void {
  const detailEl = document.getElementById('email-detail');
  if (detailEl) detailEl.innerHTML = emptyState('Select a thread on the left.');
  document.getElementById('email-pane')?.classList.remove('email-pane-show-detail');
}

async function markAllRead(): Promise<void> {
  const unreadIds = currentThreads.filter(t => t.unread).map(t => t.id);
  if (unreadIds.length === 0) {
    toast('No unread threads.', 'success');
    return;
  }
  await Promise.all(unreadIds.map(id =>
    post(`/api/email-threads/${encodeURIComponent(id)}/unread`, { value: false }).catch(() => null),
  ));
  toast(`${unreadIds.length} thread${unreadIds.length === 1 ? '' : 's'} marked read.`, 'success');
  await refresh();
}

type ComposeMode = 'new' | 'reply' | 'reply-all' | 'forward';


function splitAddrs(raw: string): string[] {
  return raw.split(/[,;]/).map(s => s.trim()).filter(s => !!s);
}

interface ComposePopupOpts {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  isHtml: boolean;
}

function closeComposePopup(): void {
  document.getElementById('gmail-compose-popup')?.remove();
}

function openComposePopup(opts: ComposePopupOpts): void {
  closeComposePopup();
  const channelId = activeChannelId;
  if (!channelId) { toast('No email channel selected.', 'error'); return; }

  const popup = document.createElement('div');
  popup.id = 'gmail-compose-popup';
  popup.className = 'compose-popup';
  popup.innerHTML = `
    <div class="compose-popup-header" id="cp-popup-header">
      <span class="compose-popup-header-title">New message</span>
      <div class="compose-popup-header-actions">
        <button class="compose-popup-header-btn" id="cp-popup-expand" type="button" title="Expand to full view">&#x26F6;</button>
        <button class="compose-popup-header-btn" id="cp-popup-close" type="button" title="Close">&times;</button>
      </div>
    </div>
    <div class="compose-popup-fields">
      <div class="compose-popup-row">
        <label class="compose-popup-label">From</label>
        <div class="compose-popup-from-wrap" style="flex:1;position:relative">
          <input type="text" id="cp-popup-from" class="compose-popup-input" placeholder="Type to search..." autocomplete="off" />
          <input type="hidden" id="cp-popup-from-id" value="" />
          <div id="cp-popup-from-dropdown" class="compose-from-dropdown" style="display:none"></div>
        </div>
      </div>
      <div class="compose-popup-row">
        <label class="compose-popup-label">To</label>
        <input type="text" id="cp-popup-to" class="compose-popup-input" value="${esc(opts.to)}" placeholder="Recipients" autocomplete="off" />
        <button type="button" class="compose-popup-cc-toggle" id="cp-popup-show-cc">Cc Bcc</button>
      </div>
      <div class="compose-popup-row compose-popup-cc-row" id="cp-popup-cc-row" style="display:${opts.cc || opts.bcc ? 'flex' : 'none'}">
        <label class="compose-popup-label">Cc</label>
        <input type="text" id="cp-popup-cc" class="compose-popup-input" value="${esc(opts.cc)}" autocomplete="off" />
      </div>
      <div class="compose-popup-row compose-popup-cc-row" id="cp-popup-bcc-row" style="display:${opts.cc || opts.bcc ? 'flex' : 'none'}">
        <label class="compose-popup-label">Bcc</label>
        <input type="text" id="cp-popup-bcc" class="compose-popup-input" value="${esc(opts.bcc)}" autocomplete="off" />
      </div>
      <div class="compose-popup-row">
        <input type="text" id="cp-popup-subject" class="compose-popup-input compose-popup-subject" value="${esc(opts.subject)}" placeholder="Subject" autocomplete="off" />
      </div>
      <div class="compose-popup-row compose-popup-tpl-row">
        <select id="cp-popup-template" class="compose-popup-input compose-popup-tpl-select">
          <option value="">No template</option>
        </select>
      </div>
      <textarea id="cp-popup-body" class="compose-popup-textarea" placeholder="Compose email">${esc(opts.body)}</textarea>
      <div class="compose-popup-html-row">
        <label class="compose-popup-html-toggle"><input type="checkbox" id="cp-popup-is-html" ${opts.isHtml ? 'checked' : ''} /> HTML</label>
      </div>
      <div class="email-attach-strip email-attach-pending-strip" id="cp-popup-attach-strip" style="display:none"></div>
    </div>
    <div class="compose-popup-footer">
      <button class="btn btn-primary compose-popup-send" id="cp-popup-send" type="button">Send</button>
      <button class="compose-popup-footer-btn" id="cp-popup-attach" type="button" title="Attach files" aria-label="Attach files">
        <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
      </button>
      <input type="file" id="cp-popup-file" multiple style="display:none" />
      <span class="compose-popup-status" id="cp-popup-status"></span>
      <div class="compose-popup-footer-right">
        <button class="compose-popup-footer-btn" id="cp-popup-discard" type="button" title="Discard">&#x1F5D1;</button>
      </div>
    </div>
  `;

  const pane = document.getElementById('email-pane') || document.body;
  pane.appendChild(popup);

  // Pending attachments for this compose (base64, sent in the request body).
  let popupAttachments: PendingAttachment[] = [];
  const popupAttachStrip = document.getElementById('cp-popup-attach-strip') as HTMLElement | null;
  const popupFileInput = document.getElementById('cp-popup-file') as HTMLInputElement | null;
  const refreshPopupChips = (): void => {
    if (popupAttachStrip) {
      renderPendingChips(popupAttachStrip, popupAttachments, idx => {
        popupAttachments.splice(idx, 1);
        refreshPopupChips();
      });
    }
  };
  document.getElementById('cp-popup-attach')?.addEventListener('click', () => popupFileInput?.click());
  popupFileInput?.addEventListener('change', async () => {
    if (!popupFileInput.files || popupFileInput.files.length === 0) return;
    popupAttachments = await addPendingFiles(popupAttachments, popupFileInput.files);
    popupFileInput.value = '';
    refreshPopupChips();
  });

  // Populate template dropdown
  const tplSelect = document.getElementById('cp-popup-template') as HTMLSelectElement | null;
  if (tplSelect) {
    void loadTemplates().then(templates => {
      for (const t of templates) {
        const opt = document.createElement('option');
        opt.value = t.id;
        opt.textContent = t.name;
        tplSelect.appendChild(opt);
      }
    });
    tplSelect.addEventListener('change', () => {
      const tplId = tplSelect.value;
      const htmlCb = document.getElementById('cp-popup-is-html') as HTMLInputElement | null;
      if (!tplId) {
        if (htmlCb) htmlCb.checked = false;
        return;
      }
      const templates = cachedTemplates || [];
      const tpl = templates.find(t => t.id === tplId);
      if (!tpl) return;
      const subjectEl = document.getElementById('cp-popup-subject') as HTMLInputElement | null;
      const bodyEl = document.getElementById('cp-popup-body') as HTMLTextAreaElement | null;
      if (tpl.subject && subjectEl && !subjectEl.value.trim()) subjectEl.value = tpl.subject;
      if (bodyEl) {
        const cur = bodyEl.value.trim();
        if (tpl.body_html.includes('{{BODY}}')) {
          bodyEl.value = tpl.body_html.replace('{{BODY}}', cur || '\n\nWrite your message here.\n\n');
        } else {
          bodyEl.value = tpl.body_html;
        }
      }
      if (htmlCb) htmlCb.checked = true;
    });
  }

  // From autocomplete — load identities + wire dropdown
  const fromInput = document.getElementById('cp-popup-from') as HTMLInputElement | null;
  const fromIdInput = document.getElementById('cp-popup-from-id') as HTMLInputElement | null;
  const fromDropdown = document.getElementById('cp-popup-from-dropdown') as HTMLElement | null;
  if (fromInput && fromIdInput && fromDropdown) {
    void loadIdentities().then(identities => {
      const renderDropdown = (filter: string): void => {
        const lf = filter.toLowerCase();
        const matches = identities.filter(id =>
          id.local_part.toLowerCase().includes(lf) ||
          id.display_name.toLowerCase().includes(lf) ||
          identityAddress(id).toLowerCase().includes(lf)
        );
        if (matches.length === 0 && !lf) {
          fromDropdown.style.display = 'none';
          return;
        }
        fromDropdown.innerHTML = matches.map(id => {
          const addr = identityAddress(id);
          const label = id.display_name ? `${esc(id.display_name)} &lt;${esc(addr)}&gt;` : esc(addr);
          const badge = id.is_noreply ? ' <span style="opacity:0.5;font-size:11px">(no-reply)</span>' : '';
          const roleBadge = id.allowed_role ? ` <span style="opacity:0.5;font-size:11px">[${esc(id.allowed_role)}]</span>` : '';
          return `<div class="compose-from-option" data-id="${esc(id.id)}" data-addr="${esc(addr)}" data-label="${esc(identityLabel(id))}">${label}${badge}${roleBadge}</div>`;
        }).join('');
        fromDropdown.style.display = matches.length > 0 ? 'block' : 'none';
        fromDropdown.querySelectorAll<HTMLElement>('.compose-from-option').forEach(opt => {
          opt.addEventListener('mousedown', (e) => {
            e.preventDefault();
            fromInput.value = opt.dataset['label'] ?? '';
            fromIdInput.value = opt.dataset['id'] ?? '';
            fromDropdown.style.display = 'none';
          });
        });
      };
      fromInput.addEventListener('focus', () => renderDropdown(fromInput.value));
      fromInput.addEventListener('input', () => renderDropdown(fromInput.value));
      fromInput.addEventListener('blur', () => { setTimeout(() => { fromDropdown.style.display = 'none'; }, 150); });
      const first = identities[0];
      if (first) {
        fromInput.value = identityLabel(first);
        fromIdInput.value = first.id;
      }
    });
  }

  // Cc/Bcc toggle
  document.getElementById('cp-popup-show-cc')?.addEventListener('click', () => {
    const ccRow = document.getElementById('cp-popup-cc-row');
    const bccRow = document.getElementById('cp-popup-bcc-row');
    if (ccRow) ccRow.style.display = ccRow.style.display === 'none' ? 'flex' : 'none';
    if (bccRow) bccRow.style.display = bccRow.style.display === 'none' ? 'flex' : 'none';
  });

  // Close
  document.getElementById('cp-popup-close')?.addEventListener('click', () => {
    void popupSaveDraft();
    closeComposePopup();
  });

  // Expand to full pane
  document.getElementById('cp-popup-expand')?.addEventListener('click', () => {
    const to = (document.getElementById('cp-popup-to') as HTMLInputElement | null)?.value ?? '';
    const cc = (document.getElementById('cp-popup-cc') as HTMLInputElement | null)?.value ?? '';
    const bcc = (document.getElementById('cp-popup-bcc') as HTMLInputElement | null)?.value ?? '';
    const subject = (document.getElementById('cp-popup-subject') as HTMLInputElement | null)?.value ?? '';
    const body = (document.getElementById('cp-popup-body') as HTMLTextAreaElement | null)?.value ?? '';
    const isHtml = (document.getElementById('cp-popup-is-html') as HTMLInputElement | null)?.checked ?? false;
    closeComposePopup();
    activeThreadId = null;
    renderComposePane({ to, cc, bcc, subject, body, isHtml, title: 'New message' });
  });

  // Discard
  document.getElementById('cp-popup-discard')?.addEventListener('click', async () => {
    if (activeDraftId) {
      await post(`/api/email-drafts/${encodeURIComponent(activeDraftId)}/delete`, {}).catch(() => {});
      activeDraftId = null;
    }
    closeComposePopup();
    if (activeView === 'drafts') void refresh();
  });

  // Auto-save
  const popupSaveDraft = async (): Promise<void> => {
    const toVal = splitAddrs((document.getElementById('cp-popup-to') as HTMLInputElement | null)?.value ?? '');
    const ccVal = splitAddrs((document.getElementById('cp-popup-cc') as HTMLInputElement | null)?.value ?? '');
    const bccVal = splitAddrs((document.getElementById('cp-popup-bcc') as HTMLInputElement | null)?.value ?? '');
    const subjectVal = (document.getElementById('cp-popup-subject') as HTMLInputElement | null)?.value.trim() ?? '';
    const bodyVal = (document.getElementById('cp-popup-body') as HTMLTextAreaElement | null)?.value ?? '';
    const htmlVal = (document.getElementById('cp-popup-is-html') as HTMLInputElement | null)?.checked ?? false;
    if (!toVal.length && !subjectVal && !bodyVal.trim()) return;
    try {
      const result = await post<EmailDraft>('/api/email-drafts/save', {
        id: activeDraftId || undefined,
        channel_id: channelId,
        to: toVal, cc: ccVal, bcc: bccVal,
        subject: subjectVal,
        body_text: htmlVal ? '' : bodyVal,
        body_html: htmlVal ? bodyVal : '',
        reply_mode: 'new',
      });
      activeDraftId = result.id;
      const s = document.getElementById('cp-popup-status');
      if (s) s.textContent = 'Saved';
      setTimeout(() => { if (s && s.textContent === 'Saved') s.textContent = ''; }, 2000);
    } catch { /* silent */ }
  };

  const schedAutoSave = (): void => {
    if (draftAutoSaveTimer) clearTimeout(draftAutoSaveTimer);
    draftAutoSaveTimer = setTimeout(() => { void popupSaveDraft(); }, 3000);
  };

  document.getElementById('cp-popup-to')?.addEventListener('input', schedAutoSave);
  document.getElementById('cp-popup-cc')?.addEventListener('input', schedAutoSave);
  document.getElementById('cp-popup-bcc')?.addEventListener('input', schedAutoSave);
  document.getElementById('cp-popup-subject')?.addEventListener('input', schedAutoSave);
  document.getElementById('cp-popup-body')?.addEventListener('input', schedAutoSave);

  // Send
  document.getElementById('cp-popup-send')?.addEventListener('click', async () => {
    const to = splitAddrs((document.getElementById('cp-popup-to') as HTMLInputElement | null)?.value ?? '');
    const cc = splitAddrs((document.getElementById('cp-popup-cc') as HTMLInputElement | null)?.value ?? '');
    const bcc = splitAddrs((document.getElementById('cp-popup-bcc') as HTMLInputElement | null)?.value ?? '');
    const subject = (document.getElementById('cp-popup-subject') as HTMLInputElement | null)?.value.trim() ?? '';
    const bodyRaw = (document.getElementById('cp-popup-body') as HTMLTextAreaElement | null)?.value ?? '';
    const isHtml = (document.getElementById('cp-popup-is-html') as HTMLInputElement | null)?.checked ?? false;
    const statusEl = document.getElementById('cp-popup-status');
    const sendBtn = document.getElementById('cp-popup-send') as HTMLButtonElement | null;

    if (to.length === 0) { if (statusEl) statusEl.textContent = 'Add recipients'; return; }
    if (!subject) { if (statusEl) statusEl.textContent = 'Add a subject'; return; }
    if (!bodyRaw.trim()) { if (statusEl) statusEl.textContent = 'Write a message'; return; }

    const fromIdentityId = (document.getElementById('cp-popup-from-id') as HTMLInputElement | null)?.value || undefined;
    if (sendBtn) sendBtn.disabled = true;
    if (statusEl) statusEl.textContent = 'Sending...';
    try {
      await post('/api/email-threads/new', {
        channel_id: channelId,
        to, cc, bcc, subject,
        body_text: isHtml ? '' : bodyRaw,
        body_html: isHtml ? bodyRaw : null,
        from_identity_id: fromIdentityId,
        attachments: popupAttachments.map(a => ({ filename: a.filename, content: a.content })),
      });
      if (activeDraftId) {
        post(`/api/email-drafts/${encodeURIComponent(activeDraftId)}/delete`, {}).catch(() => {});
        activeDraftId = null;
      }
      closeComposePopup();
      toast('Sent.', 'success');
      void refresh();
    } catch (e) {
      if (statusEl) statusEl.textContent = (e instanceof Error ? e.message : 'Send failed');
      if (sendBtn) sendBtn.disabled = false;
    }
  });

  // Focus To
  setTimeout(() => {
    (document.getElementById('cp-popup-to') as HTMLInputElement | null)?.focus();
  }, 50);
}

function openComposePaneFromThread(mode: ComposeMode, thread: ThreadDetail): void {
  activeDraftId = null;
  const last = thread.messages[thread.messages.length - 1];
  const lastInbound = [...thread.messages].reverse().find(m => m.direction === 'inbound');
  const replyTarget = lastInbound ?? last;
  const prevFrom = replyTarget?.from ?? '';
  const prevTo = replyTarget?.to ?? '';
  const prevCc = replyTarget?.cc ?? '';
  const prevSubject = last?.subject ?? thread.subject_normalized;
  const prevBody = last?.body_text ?? '';
  const prevDate = last?.received_at ?? '';

  let defTo = '';
  let defCc = '';
  let defSubject = '';
  let defBody = '';
  let title = 'New message';

  if (mode === 'reply') {
    title = 'Reply';
    defTo = prevFrom;
    defSubject = prevSubject.toLowerCase().startsWith('re:') ? prevSubject : `Re: ${prevSubject}`;
  } else if (mode === 'reply-all') {
    title = 'Reply all';
    const all = [prevFrom, ...prevTo.split(/,\s*/), ...prevCc.split(/,\s*/)]
      .map(s => s.trim())
      .filter(s => !!s && !/@the-hub-ecosystems\.com$/i.test(s));
    const uniq = Array.from(new Set(all));
    defTo = uniq.slice(0, 1).join(', ');
    defCc = uniq.slice(1).join(', ');
    defSubject = prevSubject.toLowerCase().startsWith('re:') ? prevSubject : `Re: ${prevSubject}`;
  } else if (mode === 'forward') {
    title = 'Forward';
    defSubject = prevSubject.toLowerCase().startsWith('fwd:') ? prevSubject : `Fwd: ${prevSubject}`;
    defBody = `\n\n---------- Forwarded message ----------\nFrom: ${prevFrom}\nDate: ${prevDate}\nSubject: ${prevSubject}\nTo: ${prevTo}\n\n${prevBody}`;
  }

  const replyFromHint = lastInbound?.to ?? '';

  renderComposePane({
    to: defTo,
    cc: defCc,
    bcc: '',
    subject: defSubject,
    body: defBody,
    isHtml: false,
    title,
    threadId: thread.id,
    mode,
    loi: thread.loi_gated,
    replyFromHint,
  });
}

interface ComposePaneOpts {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  isHtml: boolean;
  title: string;
  draftId?: string;
  threadId?: string;
  mode?: ComposeMode;
  loi?: boolean;
  replyFromHint?: string;
}

function renderComposePane(opts: ComposePaneOpts): void {
  const channelId = activeChannelId;
  if (!channelId) { toast('No email channel selected.', 'error'); return; }

  const detailEl = document.getElementById('email-detail');
  if (!detailEl) return;

  if (opts.draftId) activeDraftId = opts.draftId;
  const mode = opts.mode || 'new';
  const threadId = opts.threadId || null;

  if (isMobile()) {
    document.getElementById('email-pane')?.classList.add('email-pane-show-detail');
  }

  const loiField = opts.loi
    ? `<div class="compose-pane-field">
         <label class="compose-pane-label">Approval</label>
         <input type="text" id="cp-approval" class="compose-pane-input" placeholder="Type 'send it' to confirm" autocomplete="off" />
       </div>
       <div class="compose-pane-loi-warn">LOI thread. External send requires typing 'send it'.</div>`
    : '';

  detailEl.innerHTML = `
    <div class="compose-pane">
      <div class="compose-pane-header">
        <h3 class="compose-pane-title">${esc(opts.title)}</h3>
        <button class="btn-icon compose-pane-close" id="cp-close" type="button" title="Close">&times;</button>
      </div>
      <div class="compose-pane-body">
        <div class="compose-pane-field">
          <label class="compose-pane-label">Template</label>
          <select id="cp-template" class="compose-pane-input">
            <option value="">No template (plain text)</option>
          </select>
        </div>
        <div class="compose-pane-field">
          <label class="compose-pane-label">From</label>
          <div style="position:relative">
            <input type="text" id="cp-from" class="compose-pane-input" placeholder="Type to search..." autocomplete="off" />
            <input type="hidden" id="cp-from-id" value="" />
            <div id="cp-from-dropdown" class="compose-from-dropdown" style="display:none"></div>
          </div>
        </div>
        <div class="compose-pane-field">
          <label class="compose-pane-label">To</label>
          <div style="position:relative">
            <input type="text" id="cp-to" class="compose-pane-input" value="${esc(opts.to)}" placeholder="name@example.com, another@example.com" autocomplete="off" />
            <div id="cp-to-contacts" class="compose-from-dropdown" style="display:none"></div>
          </div>
        </div>
        <div class="compose-pane-field compose-pane-ccbcc" id="cp-ccbcc-toggle">
          <button type="button" class="compose-pane-ccbcc-btn" id="cp-show-cc">Cc / Bcc</button>
        </div>
        <div class="compose-pane-field compose-pane-cc-fields" id="cp-cc-fields" style="display:${opts.cc || opts.bcc ? 'flex' : 'none'}">
          <div class="compose-pane-field">
            <label class="compose-pane-label">Cc</label>
            <input type="text" id="cp-cc" class="compose-pane-input" value="${esc(opts.cc)}" placeholder="cc@example.com" autocomplete="off" />
          </div>
          <div class="compose-pane-field">
            <label class="compose-pane-label">Bcc</label>
            <input type="text" id="cp-bcc" class="compose-pane-input" value="${esc(opts.bcc)}" placeholder="bcc@example.com" autocomplete="off" />
          </div>
        </div>
        <div class="compose-pane-field">
          <label class="compose-pane-label">Subject</label>
          <input type="text" id="cp-subject" class="compose-pane-input" value="${esc(opts.subject)}" autocomplete="off" />
        </div>
        <div class="compose-pane-field compose-pane-body-field">
          <textarea id="cp-body" class="compose-pane-textarea" placeholder="Write your message...">${esc(opts.body)}</textarea>
        </div>
        <div class="compose-pane-field compose-pane-html-row">
          <label class="compose-pane-html-toggle"><input type="checkbox" id="cp-is-html" ${opts.isHtml ? 'checked' : ''} /> Send as HTML</label>
        </div>
        <div class="email-attach-strip email-attach-pending-strip" id="cp-attach-strip" style="display:none"></div>
        ${loiField}
      </div>
      <div class="compose-pane-footer">
        <span class="compose-pane-status" id="cp-status"></span>
        <button class="btn btn-secondary" id="cp-attach" type="button" title="Attach files">Attach</button>
        <input type="file" id="cp-file" multiple style="display:none" />
        <button class="btn btn-secondary" id="cp-discard" type="button">Discard</button>
        <button class="btn btn-secondary" id="cp-save-draft" type="button">Save draft</button>
        <button class="btn btn-primary" id="cp-send" type="button">Send</button>
      </div>
    </div>
  `;

  // Pending attachments for this compose (base64, sent in the request body).
  let paneAttachments: PendingAttachment[] = [];
  const paneAttachStrip = document.getElementById('cp-attach-strip') as HTMLElement | null;
  const paneFileInput = document.getElementById('cp-file') as HTMLInputElement | null;
  const refreshPaneChips = (): void => {
    if (paneAttachStrip) {
      renderPendingChips(paneAttachStrip, paneAttachments, idx => {
        paneAttachments.splice(idx, 1);
        refreshPaneChips();
      });
    }
  };
  document.getElementById('cp-attach')?.addEventListener('click', () => paneFileInput?.click());
  paneFileInput?.addEventListener('change', async () => {
    if (!paneFileInput.files || paneFileInput.files.length === 0) return;
    paneAttachments = await addPendingFiles(paneAttachments, paneFileInput.files);
    paneFileInput.value = '';
    refreshPaneChips();
  });

  // Populate template dropdown
  const tplSelect = document.getElementById('cp-template') as HTMLSelectElement | null;
  if (tplSelect) {
    loadTemplates().then(templates => {
      for (const t of templates) {
        const opt = document.createElement('option');
        opt.value = t.id;
        opt.textContent = `${t.name} — ${t.description}`;
        tplSelect.appendChild(opt);
      }
    });
    tplSelect.addEventListener('change', () => {
      const tplId = tplSelect.value;
      if (!tplId) {
        const htmlCb = document.getElementById('cp-is-html') as HTMLInputElement | null;
        if (htmlCb) htmlCb.checked = false;
        return;
      }
      const templates = cachedTemplates || [];
      const tpl = templates.find(t => t.id === tplId);
      if (!tpl) return;
      const subjectEl = document.getElementById('cp-subject') as HTMLInputElement | null;
      const bodyEl = document.getElementById('cp-body') as HTMLTextAreaElement | null;
      const htmlCb = document.getElementById('cp-is-html') as HTMLInputElement | null;
      if (tpl.subject && subjectEl && !subjectEl.value.trim()) {
        subjectEl.value = tpl.subject;
      }
      if (bodyEl) {
        const currentBody = bodyEl.value.trim();
        const placeholder = '{{BODY}}';
        if (tpl.body_html.includes(placeholder)) {
          bodyEl.value = tpl.body_html.replace(placeholder, currentBody || '\n\nWrite your message here.\n\n');
        } else {
          bodyEl.value = tpl.body_html;
        }
      }
      if (htmlCb) htmlCb.checked = true;
    });
  }

  // From autocomplete in full compose pane
  const cpFromInput = document.getElementById('cp-from') as HTMLInputElement | null;
  const cpFromIdInput = document.getElementById('cp-from-id') as HTMLInputElement | null;
  const cpFromDropdown = document.getElementById('cp-from-dropdown') as HTMLElement | null;
  if (cpFromInput && cpFromIdInput && cpFromDropdown) {
    void loadIdentities().then(identities => {
      if (opts.replyFromHint) {
        const match = findIdentityForAddress(identities, opts.replyFromHint);
        if (match) {
          cpFromInput.value = identityLabel(match);
          cpFromIdInput.value = match.id;
        }
      }
      const renderDd = (filter: string): void => {
        const lf = filter.toLowerCase();
        const matches = identities.filter(id =>
          id.local_part.toLowerCase().includes(lf) ||
          id.display_name.toLowerCase().includes(lf) ||
          identityAddress(id).toLowerCase().includes(lf)
        );
        if (matches.length === 0 && !lf) { cpFromDropdown.style.display = 'none'; return; }
        cpFromDropdown.innerHTML = matches.map(id => {
          const addr = identityAddress(id);
          const label = id.display_name ? `${esc(id.display_name)} &lt;${esc(addr)}&gt;` : esc(addr);
          const badge = id.is_noreply ? ' <span style="opacity:0.5;font-size:11px">(no-reply)</span>' : '';
          const roleBadge = id.allowed_role ? ` <span style="opacity:0.5;font-size:11px">[${esc(id.allowed_role)}]</span>` : '';
          return `<div class="compose-from-option" data-id="${esc(id.id)}" data-addr="${esc(addr)}" data-label="${esc(identityLabel(id))}">${label}${badge}${roleBadge}</div>`;
        }).join('');
        cpFromDropdown.style.display = matches.length > 0 ? 'block' : 'none';
        cpFromDropdown.querySelectorAll<HTMLElement>('.compose-from-option').forEach(opt => {
          opt.addEventListener('mousedown', (e) => {
            e.preventDefault();
            cpFromInput.value = opt.dataset['label'] ?? '';
            cpFromIdInput.value = opt.dataset['id'] ?? '';
            cpFromDropdown.style.display = 'none';
          });
        });
      };
      cpFromInput.addEventListener('focus', () => renderDd(cpFromInput.value));
      cpFromInput.addEventListener('input', () => renderDd(cpFromInput.value));
      cpFromInput.addEventListener('blur', () => { setTimeout(() => { cpFromDropdown.style.display = 'none'; }, 150); });
      const firstId = identities[0];
      if (firstId) {
        cpFromInput.value = identityLabel(firstId);
        cpFromIdInput.value = firstId.id;
      }
    });
  }

  // Cc/Bcc toggle
  document.getElementById('cp-show-cc')?.addEventListener('click', () => {
    const fields = document.getElementById('cp-cc-fields');
    const toggle = document.getElementById('cp-ccbcc-toggle');
    if (fields) fields.style.display = 'flex';
    if (toggle) toggle.style.display = 'none';
  });
  if (opts.cc || opts.bcc) {
    const toggle = document.getElementById('cp-ccbcc-toggle');
    if (toggle) toggle.style.display = 'none';
  }

  // Auto-save
  const saveDraft = async (): Promise<void> => {
    const toVal = splitAddrs((document.getElementById('cp-to') as HTMLInputElement | null)?.value ?? '');
    const ccVal = splitAddrs((document.getElementById('cp-cc') as HTMLInputElement | null)?.value ?? '');
    const bccVal = splitAddrs((document.getElementById('cp-bcc') as HTMLInputElement | null)?.value ?? '');
    const subjectVal = (document.getElementById('cp-subject') as HTMLInputElement | null)?.value.trim() ?? '';
    const bodyVal = (document.getElementById('cp-body') as HTMLTextAreaElement | null)?.value ?? '';
    const htmlVal = (document.getElementById('cp-is-html') as HTMLInputElement | null)?.checked ?? false;
    if (!toVal.length && !subjectVal && !bodyVal.trim()) return;
    try {
      const result = await post<EmailDraft>('/api/email-drafts/save', {
        id: activeDraftId || undefined,
        channel_id: channelId,
        to: toVal,
        cc: ccVal,
        bcc: bccVal,
        subject: subjectVal,
        body_text: htmlVal ? '' : bodyVal,
        body_html: htmlVal ? bodyVal : '',
        reply_mode: mode,
        reply_to_thread_id: threadId,
      });
      activeDraftId = result.id;
      const statusEl = document.getElementById('cp-status');
      if (statusEl) statusEl.textContent = 'Draft saved';
      setTimeout(() => { if (statusEl && statusEl.textContent === 'Draft saved') statusEl.textContent = ''; }, 2000);
    } catch { /* silent */ }
  };

  const scheduleAutoSave = (): void => {
    if (draftAutoSaveTimer) clearTimeout(draftAutoSaveTimer);
    draftAutoSaveTimer = setTimeout(() => { void saveDraft(); }, 3000);
  };

  // Contact autocomplete on To field
  const cpToInput = document.getElementById('cp-to') as HTMLInputElement | null;
  const cpToDropdown = document.getElementById('cp-to-contacts') as HTMLElement | null;
  if (cpToInput && cpToDropdown) {
    let contactTimer: ReturnType<typeof setTimeout> | null = null;
    cpToInput.addEventListener('input', () => {
      if (contactTimer) clearTimeout(contactTimer);
      contactTimer = setTimeout(async () => {
        const raw = cpToInput.value;
        const lastComma = raw.lastIndexOf(',');
        const query = (lastComma >= 0 ? raw.slice(lastComma + 1) : raw).trim();
        if (query.length < 2) { cpToDropdown.style.display = 'none'; return; }
        const sid = state.currentServer;
        if (!sid) return;
        try {
          const results = await get<{id: string; name: string; email: string; company: string}[]>(
            `/api/contacts/search?server_id=${encodeURIComponent(sid)}&q=${encodeURIComponent(query)}`
          );
          if (results.length === 0) { cpToDropdown.style.display = 'none'; return; }
          cpToDropdown.innerHTML = results.map(c => {
            const label = c.name ? `${esc(c.name)} &lt;${esc(c.email)}&gt;` : esc(c.email);
            const co = c.company ? ` <span style="opacity:0.5;font-size:11px">${esc(c.company)}</span>` : '';
            return `<div class="compose-from-option" data-email="${esc(c.email)}" data-label="${esc(c.name ? `${c.name} <${c.email}>` : c.email)}">${label}${co}</div>`;
          }).join('');
          cpToDropdown.style.display = 'block';
          cpToDropdown.querySelectorAll<HTMLElement>('.compose-from-option').forEach(opt => {
            opt.addEventListener('mousedown', (e) => {
              e.preventDefault();
              const prefix = lastComma >= 0 ? raw.slice(0, lastComma + 1) + ' ' : '';
              cpToInput.value = prefix + (opt.dataset['label'] ?? '');
              cpToDropdown.style.display = 'none';
            });
          });
        } catch { cpToDropdown.style.display = 'none'; }
      }, 250);
    });
    cpToInput.addEventListener('blur', () => {
      setTimeout(() => { cpToDropdown.style.display = 'none'; }, 200);
    });
  }

  document.getElementById('cp-to')?.addEventListener('input', scheduleAutoSave);
  document.getElementById('cp-cc')?.addEventListener('input', scheduleAutoSave);
  document.getElementById('cp-bcc')?.addEventListener('input', scheduleAutoSave);
  document.getElementById('cp-subject')?.addEventListener('input', scheduleAutoSave);
  document.getElementById('cp-body')?.addEventListener('input', scheduleAutoSave);

  // Close → back to thread or empty
  document.getElementById('cp-close')?.addEventListener('click', () => {
    void saveDraft();
    if (activeThreadId) void renderDetail();
    else clearDetail();
  });

  // Discard
  document.getElementById('cp-discard')?.addEventListener('click', async () => {
    if (activeDraftId) {
      await post(`/api/email-drafts/${encodeURIComponent(activeDraftId)}/delete`, {}).catch(() => {});
      activeDraftId = null;
    }
    if (activeView === 'drafts') void refresh();
    clearDetail();
  });

  // Save draft
  document.getElementById('cp-save-draft')?.addEventListener('click', async () => {
    await saveDraft();
    toast('Draft saved', 'success');
    if (activeView === 'drafts') void refresh();
  });

  // Send
  document.getElementById('cp-send')?.addEventListener('click', async () => {
    const to = splitAddrs((document.getElementById('cp-to') as HTMLInputElement | null)?.value ?? '');
    const cc = splitAddrs((document.getElementById('cp-cc') as HTMLInputElement | null)?.value ?? '');
    const bcc = splitAddrs((document.getElementById('cp-bcc') as HTMLInputElement | null)?.value ?? '');
    const subject = (document.getElementById('cp-subject') as HTMLInputElement | null)?.value.trim() ?? '';
    const bodyRaw = (document.getElementById('cp-body') as HTMLTextAreaElement | null)?.value ?? '';
    const isHtml = (document.getElementById('cp-is-html') as HTMLInputElement | null)?.checked ?? false;
    const approval = (document.getElementById('cp-approval') as HTMLInputElement | null)?.value ?? '';
    const statusEl = document.getElementById('cp-status');
    const sendBtn = document.getElementById('cp-send') as HTMLButtonElement | null;

    if (to.length === 0) { if (statusEl) statusEl.textContent = 'To required.'; return; }
    if (mode === 'new' && !subject) { if (statusEl) statusEl.textContent = 'Subject required.'; return; }
    if (!bodyRaw.trim()) { if (statusEl) statusEl.textContent = 'Body required.'; return; }

    const cpFromId = (document.getElementById('cp-from-id') as HTMLInputElement | null)?.value || undefined;
    const attachments = paneAttachments.map(a => ({ filename: a.filename, content: a.content }));
    if (sendBtn) sendBtn.disabled = true;
    if (statusEl) statusEl.textContent = 'Sending…';
    try {
      if (mode === 'forward' && threadId) {
        await post(`/api/email-threads/${encodeURIComponent(threadId)}/forward`, {
          to, cc, bcc,
          body_text: isHtml ? '' : bodyRaw,
          body_html: isHtml ? bodyRaw : null,
          approval,
          attachments,
        });
      } else if ((mode === 'reply' || mode === 'reply-all') && threadId) {
        if (cc.length === 0 && bcc.length === 0 && to.length === 1) {
          await post(`/api/email-threads/${encodeURIComponent(threadId)}/reply`, {
            body_text: isHtml ? '' : bodyRaw,
            body_html: isHtml ? bodyRaw : null,
            approval,
            from_identity_id: cpFromId,
            attachments,
          });
        } else {
          await post('/api/email-threads/new', {
            channel_id: channelId,
            to, cc, bcc, subject,
            body_text: isHtml ? '' : bodyRaw,
            body_html: isHtml ? bodyRaw : null,
            approval,
            from_identity_id: cpFromId,
            attachments,
          });
        }
      } else {
        await post('/api/email-threads/new', {
          channel_id: channelId,
          to, cc, bcc, subject,
          body_text: isHtml ? '' : bodyRaw,
          body_html: isHtml ? bodyRaw : null,
          approval,
          from_identity_id: cpFromId,
          attachments,
        });
      }
      if (activeDraftId) {
        post(`/api/email-drafts/${encodeURIComponent(activeDraftId)}/delete`, {}).catch(() => {});
        activeDraftId = null;
      }
      toast('Sent.', 'success');
      await refresh();
      clearDetail();
    } catch (e) {
      if (statusEl) statusEl.textContent = (e instanceof Error ? e.message : 'Send failed');
      if (sendBtn) sendBtn.disabled = false;
    }
  });

  // Focus the To field on open
  setTimeout(() => {
    (document.getElementById('cp-to') as HTMLInputElement | null)?.focus();
  }, 50);
}

interface EmailTemplateRow {
  id: string;
  name: string;
  description: string;
  subject: string;
  body_html: string;
  category: string;
  display_order: number;
}

let cachedTemplates: EmailTemplateRow[] | null = null;

async function loadTemplates(): Promise<EmailTemplateRow[]> {
  if (cachedTemplates) return cachedTemplates;
  try {
    cachedTemplates = await get<EmailTemplateRow[]>('/api/email-templates');
  } catch {
    cachedTemplates = [];
  }
  return cachedTemplates;
}


interface EmailIdentity {
  id: string;
  server_id: string;
  local_part: string;
  domain: string;
  display_name: string;
  is_noreply: number;
  allowed_role: string;
  created_at: string;
}

let cachedIdentities: EmailIdentity[] | null = null;

async function loadIdentities(): Promise<EmailIdentity[]> {
  if (cachedIdentities) return cachedIdentities;
  const sid = state.currentServer;
  if (!sid) { cachedIdentities = []; return []; }
  try {
    cachedIdentities = await get<EmailIdentity[]>(`/api/email-identities?server_id=${encodeURIComponent(sid)}`);
  } catch {
    cachedIdentities = [];
  }
  return cachedIdentities;
}

function identityAddress(id: EmailIdentity): string {
  return `${id.local_part}@${id.domain}`;
}

function identityLabel(id: EmailIdentity): string {
  const addr = identityAddress(id);
  if (id.display_name) return `${id.display_name} <${addr}>`;
  return addr;
}

function findIdentityForAddress(identities: EmailIdentity[], raw: string): EmailIdentity | null {
  const parts = raw.toLowerCase().split(/,\s*/);
  for (const id of identities) {
    const addr = identityAddress(id).toLowerCase();
    if (parts.some(p => p.includes(addr))) return id;
  }
  return null;
}

async function fetchDrafts(channelId: string): Promise<EmailDraft[]> {
  return get<EmailDraft[]>(`/api/email-drafts?channel_id=${encodeURIComponent(channelId)}`);
}

function renderDraftRow(d: EmailDraft): string {
  const to: string[] = JSON.parse(d.to_json || '[]');
  const toLabel = to.length > 0 ? to.join(', ') : '(no recipients)';
  const subj = d.subject || '(no subject)';
  const snippet = (d.body_text || '').slice(0, 80);
  const when = relativeTime(d.updated_at);
  return `
    <div class="email-row email-draft-row" data-draft-id="${esc(d.id)}">
      <div class="email-row-main">
        <div class="email-row-from">${esc(toLabel)}</div>
        <div class="email-row-subject">${esc(subj)}</div>
        <div class="email-row-snippet">${esc(snippet)}</div>
      </div>
      <div class="email-row-meta">
        <span class="email-row-time">${esc(when)}</span>
        <button class="email-draft-delete-btn" data-draft-id="${esc(d.id)}" title="Discard draft">&times;</button>
      </div>
    </div>
  `;
}

function renderDraftList(target: HTMLElement, drafts: EmailDraft[]): void {
  if (drafts.length === 0) {
    target.innerHTML = `<div class="email-empty"><p>No drafts.</p></div>`;
    return;
  }
  target.innerHTML = drafts.map(renderDraftRow).join('');
  target.querySelectorAll<HTMLElement>('.email-draft-row').forEach(row => {
    row.addEventListener('click', (e) => {
      const deleteBtn = (e.target as HTMLElement).closest('.email-draft-delete-btn');
      if (deleteBtn) return;
      const draftId = row.dataset['draftId'];
      if (draftId) openDraftInCompose(draftId, drafts);
    });
  });
  target.querySelectorAll<HTMLButtonElement>('.email-draft-delete-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.stopPropagation();
      const draftId = btn.dataset['draftId'];
      if (!draftId) return;
      await post(`/api/email-drafts/${encodeURIComponent(draftId)}/delete`, {});
      void refresh();
    });
  });
}

function openDraftInCompose(draftId: string, drafts: EmailDraft[]): void {
  const draft = drafts.find(d => d.id === draftId);
  if (!draft) return;
  activeDraftId = draft.id;
  const to: string[] = JSON.parse(draft.to_json || '[]');
  const cc: string[] = JSON.parse(draft.cc_json || '[]');
  const bcc: string[] = JSON.parse(draft.bcc_json || '[]');
  openComposePopup({
    to: to.join(', '),
    cc: cc.join(', '),
    bcc: bcc.join(', '),
    subject: draft.subject,
    body: draft.body_text || draft.body_html,
    isHtml: !!draft.body_html && !draft.body_text,
  });
}

async function refresh(): Promise<void> {
  if (!activeChannelId) return;
  const listBody = document.getElementById('email-thread-list-body');
  if (!listBody) return;
  const channelId = activeChannelId;
  if (activeView === 'drafts') {
    const drafts = await fetchDrafts(channelId);
    if (activeChannelId !== channelId) return;
    renderDraftList(listBody, drafts);
    return;
  }
  const threads = searchTerm
    ? await fetchSearch(channelId, searchTerm)
    : await fetchThreads(channelId, activeView);
  if (activeChannelId !== channelId || !listBody) return;
  renderThreadList(listBody, threads);
}

export async function renderEmailInbox(body: HTMLElement): Promise<void> {
  cleanupEmailInbox();
  const channelId = findChannelId();
  activeChannelId = channelId;
  activeView = 'inbox';
  searchTerm = '';

  if (!channelId) {
    body.innerHTML = emptyState('Email channel not provisioned.');
    return;
  }

  body.innerHTML = `
    <div class="email-pane" id="email-pane">
      <aside class="email-sidebar" id="email-sidebar">
        <div class="email-toolbar">
          <div class="email-toolbar-row">
            <button class="btn btn-primary" id="email-compose-btn" type="button">Compose</button>
            <button class="btn btn-secondary btn-sm" id="email-refresh-btn" type="button" title="Refresh">\u21bb</button>
            <button class="btn btn-secondary btn-sm" id="email-markread-btn" type="button" title="Mark all read">\u2713</button>
            <button class="btn btn-secondary btn-sm" id="email-backfill-btn" type="button" title="Sync inbox">Sync</button>
          </div>
          <div class="email-toolbar-row">
            <input type="search" id="email-search" class="email-search" placeholder="Search subject / from / body" autocomplete="off" />
          </div>
          <div class="email-toolbar-row email-view-chips" role="tablist">
            <button class="email-view-chip email-view-active" data-view="inbox" role="tab" type="button">Inbox</button>
            <button class="email-view-chip" data-view="drafts" role="tab" type="button">Drafts</button>
            <button class="email-view-chip" data-view="starred" role="tab" type="button">Starred</button>
            <button class="email-view-chip" data-view="archived" role="tab" type="button">Archived</button>
            <button class="email-view-chip" data-view="trash" role="tab" type="button">Trash</button>
          </div>
        </div>
        <div class="email-thread-list-body" id="email-thread-list-body">
          <div class="email-empty"><p>Loading\u2026</p></div>
        </div>
      </aside>
      <section class="email-detail" id="email-detail">
        <div class="email-empty"><p>Select a thread on the left.</p></div>
      </section>
    </div>
  `;

  const listBody = document.getElementById('email-thread-list-body');
  if (listBody) {
    const threads = await fetchThreads(channelId, activeView);
    if (activeChannelId !== channelId) return;
    // Auto-open the first thread on desktop only.
    if (!isMobile() && !activeThreadId && threads.length > 0 && threads[0]) {
      activeThreadId = threads[0].id;
    }
    renderThreadList(listBody, threads);
    if (activeThreadId && !isMobile()) {
      void renderDetail();
    } else {
      clearDetail();
    }
  }

  document.getElementById('email-compose-btn')?.addEventListener('click', () => {
    activeDraftId = null;
    openComposePopup({ to: '', cc: '', bcc: '', subject: '', body: '', isHtml: false });
  });
  document.getElementById('email-refresh-btn')?.addEventListener('click', () => { void refresh(); });
  document.getElementById('email-markread-btn')?.addEventListener('click', () => { void markAllRead(); });
  document.getElementById('email-backfill-btn')?.addEventListener('click', async () => {
    const btn = document.getElementById('email-backfill-btn') as HTMLButtonElement | null;
    if (!btn) return;
    btn.disabled = true;
    const prev = btn.textContent ?? 'Sync';
    btn.textContent = 'Syncing\u2026';
    try {
      await post('/api/email-threads/backfill', {});
      await refresh();
    } catch (e) { toast(e instanceof Error ? e.message : 'Sync failed', 'error'); }
    finally { btn.disabled = false; btn.textContent = prev; }
  });

  const searchEl = document.getElementById('email-search') as HTMLInputElement | null;
  if (searchEl) {
    let timer: ReturnType<typeof setTimeout> | null = null;
    searchEl.addEventListener('input', () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        searchTerm = searchEl.value;
        void refresh();
      }, 200);
    });
  }

  document.querySelectorAll<HTMLButtonElement>('.email-view-chip').forEach(btn => {
    btn.addEventListener('click', async () => {
      document.querySelectorAll('.email-view-chip').forEach(n => n.classList.remove('email-view-active'));
      btn.classList.add('email-view-active');
      activeView = (btn.dataset['view'] as View) ?? 'inbox';
      searchTerm = '';
      if (searchEl) searchEl.value = '';
      activeThreadId = null;
      clearDetail();
      await refresh();
    });
  });

  pollTimer = setInterval(() => { void refresh(); }, 30_000);
}
